import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { auth } from '../lib/firebase';
import { getCompletedGames, deleteGame } from '../lib/gameService';
import { TopNav } from '../components/TopNav';
import { useNavigate } from 'react-router-dom';
import { History as HistoryIcon, Calendar, ChevronRight, Trash2 } from 'lucide-react';
import clsx from 'clsx';

export const History = () => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    const hostId = auth.currentUser?.uid;

    useEffect(() => {
        const fetch = async () => {
            if (!hostId) return;
            try {
                const games = await getCompletedGames(hostId);
                setSessions(games);
            } catch (err) {
                console.error('Error fetching history:', err);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [hostId]);

    const handleDelete = async (id) => {
        if (!hostId) return;
        if (!window.confirm('האם למחוק את הסשן? פעולה זו בלתי הפיכה.')) return;
        // Optimistic update
        setSessions(prev => prev.filter(s => s.id !== id));
        try { await deleteGame(hostId, id); }
        catch (err) {
            console.error('Delete failed:', err);
            // Re-fetch on failure
            getCompletedGames(hostId).then(setSessions).catch(console.error);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString(
            language === 'he' ? 'he-IL' : 'en-US',
            { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
        );
    };

    const formatCurrency = (amount) => `₪${(amount || 0).toLocaleString()}`;

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
            <TopNav />

            <div className="flex flex-col flex-1 min-h-0 w-full max-w-xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="mb-4 flex-shrink-0 flex items-center gap-2">
                    <HistoryIcon className="w-5 h-5 text-amber-500" />
                    <div>
                        <h1 className="text-[clamp(1.4rem,4vw,1.8rem)] font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 leading-none font-heebo">
                            {t('navHistory')}
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5 font-heebo">{t('historySubtitle')}</p>
                    </div>
                </div>

                {/* Scrollable session list */}
                <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pb-4">
                    {loading ? (
                        [1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-slate-900/50 rounded-xl animate-pulse" />)
                    ) : sessions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-600">
                            <Calendar className="w-10 h-10 mb-3 opacity-30" />
                            <p className="text-sm font-heebo">{t('noHistoryFound')}</p>
                            <p className="text-xs text-slate-700 mt-1 font-heebo">{t('historyEmptyHelp')}</p>
                        </div>
                    ) : sessions.map(session => {
                        const net = session.netBalance || 0;
                        return (
                            <div
                                key={session.id}
                                className="w-full flex items-center gap-4 px-4 py-3.5 bg-slate-900/60 border border-white/5 rounded-xl hover:border-amber-500/20 hover:shadow-[0_0_12px_rgba(245,158,11,0.15)] transition-all"
                            >
                                {/* Clickable area */}
                                <button
                                    onClick={() => navigate(`/session/${session.id}`)}
                                    className="flex items-center gap-4 flex-1 min-w-0 text-right active:scale-[0.99]"
                                >
                                    {/* Net balance */}
                                    <span className={clsx(
                                        'font-mono text-sm font-black tabular-nums flex-shrink-0 font-inter',
                                        net >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                    )}>
                                        {net >= 0 ? '+' : ''}{formatCurrency(net)}
                                    </span>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-white truncate font-heebo">
                                            {formatDate(session.startTime)}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 justify-end flex-wrap">
                                            {session.stakes && (
                                                <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded font-heebo">
                                                    {session.stakes}
                                                </span>
                                            )}
                                            <span className="text-[10px] font-bold text-slate-500 bg-slate-800 border border-white/5 px-1.5 py-0.5 rounded font-heebo">
                                                {t('historyCompleted')}
                                            </span>
                                        </div>
                                    </div>

                                    <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0 rtl:rotate-180" />
                                </button>

                                {/* Delete button */}
                                <button
                                    onClick={() => handleDelete(session.id)}
                                    className="flex-shrink-0 p-1.5 text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                    title="מחק סשן"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
